import React, { useState, useEffect, useContext } from 'react';
import { withRouter, Route, Switch, Redirect } from 'react-router-dom';
import _ from 'lodash';
import GenericListPair from 'modules/common/components/GenericListPair';
import ConfirmAlert from 'modules/common/components/ConfirmAlert';
import { connect } from 'react-redux';
import { ModalContext } from 'App';
import PropTypes from 'prop-types';
import { getJson } from 'session';
import useDebounce from 'modules/common/components/UseDebounce';
import CreateModal from '../components/CreateModal';
import UpdateModal from '../components/UpdateModal';
import CreatePermissionModal from '../components/CreatePermissionModal';
import RoleManagementContent from './RolesManagementContent';
import * as actions from '../action';
import * as c from '../constant';

const form = {
  keyword: '',
};

function RoleManagementContainer({
  match,
  isLoading,
  history,
  getRoleList,
  roleList,
  roleListPager,
  removeRole,
  removePermission,
  getPermissionList,
  showRole
}) {
  const [formData, setFormData] = useState(form);
  const setModal = useContext(ModalContext);

  const profile = getJson('profile') || {};

  const handleOnChange = (e) => {
    setFormData({
      ...formData,
      q: e.target.value,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddModal = (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: <CreateModal />,
      title: 'Add Role',
      modalSize: 'modal-md',
    });
  };

  const handleEdit = (data) => (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: <UpdateModal updateList={data.attributes} id={data.id} />,
      title: 'Update Role',
      modalSize: 'modal-md',
    });
  };

  const handleRemove = (data) => (e) => {
    e.preventDefault();
    ConfirmAlert(
      'Are you sure you want to remove this role?',
      () => {
        removeRole(data.id, () => {
          getRoleList();
          const id = _.get(roleList, '1.id');
          history.push(`/role-management/${id}`);
          showRole(id);
        });
      },
      () => { },
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel',
      }
    );
  };

  const handleAddPermissionModal = (id) => () => {
    setModal({
      isOpen: true,
      content: (
        <CreatePermissionModal
          id={id}
        />
      ),
      title: 'Add Permission',
      modalSize: 'modal-md',
    });
  };

  const handleRemovePermission = (roleId, data) => (e) => {
    e.preventDefault();
    ConfirmAlert(
      'Are you sure you want to remove this permission?',
      () => {
        removePermission(roleId, data.id, () => {
          getPermissionList(roleId);
        });
      },
      () => { },
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel',
      }
    );
  };

  useEffect(() => {
    getRoleList();
    return () => {
      setModal({ isOpen: false });
    };
  }, []);

  const debouncedSearchTerm = useDebounce(formData, 500);

  const param = {
    q: formData.keyword,
    sort_by: 'asc'
  };

  useEffect(() => {
    if (debouncedSearchTerm) {
      getRoleList({ is_paginated: 1, q: formData.keyword, sort_by: 'desc' });
    } else {
      setFormData([]);
    }
  }, [debouncedSearchTerm]);
  return (
    <div className="card-content-listing">
      <Route
        path={`${match.path}/:roleId?`}
        render={({ match: { params } }) => (
          <GenericListPair
            moduleName="Role Management"
            getData={getRoleList}
            setParams={param}
            setKeyword={handleOnChange}
            keyword={_.get(formData, 'keyword') || ''}
            keywordPlaceholder="Search Role..."
            pathName={`${match.path}/:roleId?`}
            listName="Roles"
            label="Roles List"
            renderItem={(item) => (
              <div className="p-3">
                <div className="transaction-list-company">
                  {_.get(item, 'attributes.name')}
                </div>
              </div>
            )}
            isLoading={isLoading}
            selected={params.roleId}
            data={_.get(profile, 'roles') === 'super_administrator' ? roleList : _.filter(roleList,
              (v) => !_.includes('super_administrator', _.get(v, 'attributes.name'))
            )}
            dataPager={roleListPager}
            handleSelectRow={(data) => {
              history.push(`${match.url}/${_.get(data, 'id')}`);
            }}
            renderContent={() => (
              <React.Fragment>
                <Switch>
                  <Route
                    path={`${match.path}/:roleId?`}
                    render={() => (
                      <RoleManagementContent
                        handleAddModal={handleAddModal}
                        handleEdit={handleEdit}
                        handleRemove={handleRemove}
                        handleAddPermissionModal={handleAddPermissionModal}
                        handleRemovePermission={handleRemovePermission}
                      />
                    )}
                  />
                </Switch>
                <Route
                  path={`${match.path}/:roleId?`}
                  render={() =>
                    !params.roleId && roleList.length > 0 ? (
                      <Redirect
                        to={`${match.url}/${_.get(roleList, '0.id')}`}
                      />
                    ) : null
                  }
                />
              </React.Fragment>
            )}
          />
        )}
      />
    </div>
  );
}

RoleManagementContainer.propTypes = {
  match: PropTypes.instanceOf(Object).isRequired,
  isLoading: PropTypes.bool.isRequired,
  history: PropTypes.instanceOf(Object).isRequired,
  getRoleList: PropTypes.instanceOf(Function).isRequired,
  roleList: PropTypes.instanceOf(Array).isRequired,
  roleListPager: PropTypes.instanceOf(Object).isRequired,
  removeRole: PropTypes.instanceOf(Function).isRequired,
  removePermission: PropTypes.instanceOf(Function).isRequired,
  getPermissionList: PropTypes.instanceOf(Function).isRequired,
  showRole: PropTypes.instanceOf(Function).isRequired,
};

const mapStateToProps = ({ api }) => ({
  isLoading: api.loading.indexOf(c.GET_ROLE) > -1,
  roleList: _.get(api, `${c.GET_ROLE}.list`) || [],
  roleListPager: _.get(api, `${c.GET_ROLE}.pager`) || {},
});

const enhance = _.flowRight([withRouter, connect(mapStateToProps, actions)]);

export default enhance(RoleManagementContainer);
